import { useTranslation } from 'react-i18next'
import {
  useDeleteEmcComplianceFileSlotAttachment,
  useGetEmcComplianceFileSlot,
  useUploadEmcComplianceFileSlot,
  type EmcProductComplianceFileEvidenceSlot,
} from '@/shared/hooks/api/emcProductComplianceFileAttachments'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { ProductComplianceFileSlotStep } from '@/v2/components/product-compliance-briefing/ProductComplianceFileSlotStep'
import type { EmcBriefingStep } from '../../utils/emcLegislationBriefing'
import { V2EmcLegislationBriefingStepShell } from './V2EmcLegislationBriefingStepShell'

const EMC_SLOT_STEP_KEYS = {
  TEST_REPORT_ELECTROMAGNETIC_DISTURBANCE: 'testReportElectromagneticDisturbance',
  TEST_REPORT_ELECTROMAGNETIC_IMMUNITY: 'testReportElectromagneticImmunity',
} as const satisfies Record<
  Exclude<
    EmcProductComplianceFileEvidenceSlot,
    'DRAWINGS_SCHEMAS' | 'DESCRIPTIONS_AND_EXPLANATIONS'
  >,
  EmcBriefingStep['key']
>

type EmcTestReportComplianceFileEvidenceSlot = keyof typeof EMC_SLOT_STEP_KEYS

export function V2EmcComplianceFileSlotStep({
  step,
  index,
  productId,
  slotKind,
}: {
  step: EmcBriefingStep
  index: number
  productId: string
  slotKind: EmcTestReportComplianceFileEvidenceSlot
}) {
  const { t } = useTranslation('common')
  const canWrite = useCanWriteOrgData()
  const evidenceSlotTitle = t(
    `v2.pages.products.detail.emcBriefing.steps.items.${EMC_SLOT_STEP_KEYS[slotKind]}.title`
  )
  const { data, isLoading } = useGetEmcComplianceFileSlot(productId, slotKind)
  const { mutateAsync: uploadFile } = useUploadEmcComplianceFileSlot(
    productId,
    slotKind
  )
  const { mutateAsync: deleteFile, isPending: isDeletePending } =
    useDeleteEmcComplianceFileSlotAttachment(productId, slotKind)

  return (
    <ProductComplianceFileSlotStep
      canWrite={canWrite}
      isLoading={isLoading}
      files={data?.files ?? []}
      isDeletePending={isDeletePending}
      evidenceSlotTitle={evidenceSlotTitle}
      onUploadFile={uploadFile}
      onDeleteFile={deleteFile}
      stepShell={({ actions, children }) => (
        <V2EmcLegislationBriefingStepShell
          step={step}
          index={index}
          actions={actions}
        >
          {children}
        </V2EmcLegislationBriefingStepShell>
      )}
    />
  )
}
